import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ContactMetaModel, SignupFirstContactDto, SignupFirstContactMeta } from '@well/domain';
import { SelectItem } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { PersonInputsComponent } from '@well/person-management';
import { DropdownUtilsService } from "@irian-ro/util-translate"
import { FormUtils } from '@irian-ro/util-forms';

@Component({
  selector: 'signup-first-contact-inputs',
  templateUrl: './first-contact-inputs.component.html',
  styleUrls: ['./first-contact-inputs.component.scss'],
})
export class FirstContactInputsComponent implements OnInit, OnDestroy {

  form: FormGroup | undefined = undefined;

  contactMeta: ContactMetaModel | undefined = undefined;

  private _meta: SignupFirstContactMeta | undefined = undefined;
  private _firstContact: SignupFirstContactDto | undefined = undefined;

  @Input() set meta(value: SignupFirstContactMeta){

    if(value){
      this._meta = value;
      if(value.contactMethods) {
        this.contactMethods = this.dropdownUtilsService.getDropdownTranslations(value.contactMethods, 'signup.firstContact.contactMethods');
      }
    }

  }

  @Input() set firstContact(value: SignupFirstContactDto){

    if(value){
      this._firstContact = value;
      FormUtils.removeEmpty(value);
      this.form?.patchValue(value);
    }

  }

  @Output() save: EventEmitter<SignupFirstContactDto> = new EventEmitter<SignupFirstContactDto>();

  constructor(private dropdownUtilsService: DropdownUtilsService) {
    this.form = this.createForm();
  }

  phoneNumberRequired = false;
  emailRequired = false;
  contactMethodRequired = true;

  visibilityTypes: SelectItem[] = [];
  contactMethods: SelectItem[] = [];

  private unsubscribe: Subject<boolean> = new Subject<boolean>();

  private personForm: PersonInputsComponent | null = null;

  @ViewChild('personForm', {static: false}) set person(personForm: PersonInputsComponent) {
    setTimeout(() => {
      this.personForm = personForm;

      if (this.personForm) {
        this.form?.setControl('personDTO', personForm.form);
      }
    }, 0);
  }

  ngOnInit(): void {
    this.subscribeToContactMethodValueChanges();
    this.subscribeToReceiveRequestsValueChanges();
  }

  get contactMethodFormControl(): FormControl {
    return this.form?.get('contactMethod') as FormControl;
  }

  get phoneNumberFormControl(): FormControl {
    return this.form?.get('phoneNumber') as FormControl;
  }

  get emailFormControl(): FormControl {
    return this.form?.get('email') as FormControl;
  }

  get receivesRequestsFormControl(): FormControl {
    return this.form?.get('receivesRequests') as FormControl;
  }

  get visibilityTypeFormControl(): FormControl {
    return this.form?.get('visibilityType') as FormControl;
  }

  onSubmit(){
    if(this.form) {
      FormUtils.markAllAsDirty(this.form);
      FormUtils.markAllAsTouched(this.form);
    }
    this.form?.updateValueAndValidity();
    if(this.form?.valid){
      this.save.emit(this.form.getRawValue());
    }
  }

  private clearValidators(): void {
    this.phoneNumberFormControl.clearValidators();
    this.phoneNumberFormControl.updateValueAndValidity();
    this.emailFormControl.clearValidators();
    this.emailFormControl.updateValueAndValidity();
    this.contactMethodFormControl.clearValidators();
    this.contactMethodFormControl.updateValueAndValidity({emitEvent: false});
    this.contactMethodRequired = false;
    this.emailRequired = false;
    this.phoneNumberRequired = false;
  }

  private createForm(): FormGroup{
    return new FormGroup({
      phoneNumber: new FormControl(null),
      email: new FormControl(null),
      receivesRequests: new FormControl(false),
      businessProfileUuid: new FormControl(null),
      visibilityType: new FormControl(null, Validators.required),
      contactMethod: new FormControl(null,Validators.required),
      personDTO: new FormControl(null, Validators.required),
      legalEntityUuid: new FormControl(null),
      validFrom: new FormControl(null, Validators.required),
      validTo: new FormControl(null),
      department: new FormControl(null)
    });
  }

  private subscribeToReceiveRequestsValueChanges(): void {
    this.receivesRequestsFormControl.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(res => {
        if (!res) {
          this.contactMethodFormControl.setValue(null);
          this.clearValidators();
        } else {
          this.contactMethodFormControl.setValidators(Validators.required);
          this.contactMethodFormControl.updateValueAndValidity({emitEvent: false});
          this.contactMethodRequired = true;
        }
      })
  }

  private subscribeToContactMethodValueChanges(): void {
    this.contactMethodFormControl.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(res => {
        if (res === 'TEL') {
          this.phoneNumberFormControl.setValidators(Validators.required);
          this.phoneNumberFormControl.updateValueAndValidity();
          this.emailFormControl.clearValidators();
          this.emailFormControl.updateValueAndValidity();
          this.phoneNumberRequired = true;
          this.emailRequired = false;
        } else if (res === 'EMAIL') {
          this.emailFormControl.setValidators(Validators.required);
          this.emailFormControl.updateValueAndValidity();
          this.phoneNumberFormControl.clearValidators();
          this.phoneNumberFormControl.updateValueAndValidity();
          this.phoneNumberRequired = false;
          this.emailRequired = true;
        } else {
          this.clearValidators();
        }
      });
  }

  ngOnDestroy(){
    this.unsubscribe.next(true);
  }

}
